import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BRAND } from "@/data/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCATIONS = [
  "Malviya Nagar", "C-Scheme", "Civil Lines", "Vaishali Nagar", "Raja Park",
  "Bani Park", "Mansarovar", "Jagatpura", "Durgapura", "Tonk Road",
  "Shyam Nagar", "Nirman Nagar", "Vidyadhar Nagar", "Ajmer Road", "Pratap Nagar",
];

const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function EntryForm() {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [otherLocation, setOtherLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isOther = location === "Other";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalLocation = isOther ? otherLocation.trim() : location;
    if (!name.trim()) return toast.error("Please enter your name.");
    if (!PHONE_REGEX.test(mobile)) return toast.error("Enter a valid 10-digit mobile number.");
    if (email && !EMAIL_REGEX.test(email)) return toast.error("Enter a valid email address.");
    if (!finalLocation) return toast.error("Please select or enter your location.");

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { name: name.trim(), mobile, email: email.trim(), location: finalLocation });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="entry-form-success" className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F9FAFB] to-white px-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md text-center rounded-3xl border border-gray-200 bg-white p-10 shadow-sm">
          <CheckCircle2 className="mx-auto text-[#D81B60] mb-4" size={48} />
          <h1 className="font-extrabold text-2xl mb-2">Thank you!</h1>
          <p className="text-gray-500 text-sm">Your details have been recorded. We look forward to seeing you at The Market Place.</p>
          <button
            data-testid="entry-form-another-button"
            onClick={() => { setName(""); setMobile(""); setEmail(""); setLocation(""); setOtherLocation(""); setDone(false); }}
            className="mt-6 rounded-full border border-gray-300 px-6 py-2.5 text-sm hover:border-[#D81B60] hover:text-[#D81B60] transition-colors duration-300"
          >
            Submit another entry
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="entry-form-page" className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F9FAFB] to-white px-6 py-12">
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-8 shadow-sm"
      >
        <img src={BRAND.logo} alt={`${BRAND.name} logo`} className="h-14 mx-auto mb-5" />
        <h1 className="font-extrabold text-2xl text-center mb-1">Welcome!</h1>
        <p className="text-gray-500 text-sm text-center mb-7">Please share a few quick details with us.</p>

        <div className="space-y-5">
          <div>
            <Label>Name *</Label>
            <Input data-testid="entry-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 focus-visible:ring-[#D81B60]" />
          </div>
          <div>
            <Label>Mobile *</Label>
            <Input
              data-testid="entry-mobile-input"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit mobile number"
              maxLength={10}
              inputMode="numeric"
              className="mt-1.5 focus-visible:ring-[#D81B60]"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" data-testid="entry-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Optional" className="mt-1.5 focus-visible:ring-[#D81B60]" />
          </div>
          <div>
            <Label>Location</Label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger data-testid="entry-location-select" className="mt-1.5">
                <SelectValue placeholder="Select your area" />
              </SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            {isOther && (
              <Input
                data-testid="entry-other-location-input"
                value={otherLocation}
                onChange={(e) => setOtherLocation(e.target.value)}
                placeholder="Please specify your location"
                className="mt-2.5 focus-visible:ring-[#D81B60]"
              />
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="entry-submit-button"
          className="w-full mt-8 rounded-full bg-[#D81B60] text-white py-3 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300 disabled:opacity-60"
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </motion.form>
    </div>
  );
}
